import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import trCommon from './locales/tr/common.json'
import trAuth from './locales/tr/auth.json'
import trNav from './locales/tr/nav.json'
import trDashboard from './locales/tr/dashboard.json'
import trCustomers from './locales/tr/customers.json'
import trProposals from './locales/tr/proposals.json'
import trProposalTemplates from './locales/tr/proposalTemplates.json'
import trProcurement from './locales/tr/procurement.json'
import trProjects from './locales/tr/projects.json'
import trTimesheets from './locales/tr/timesheets.json'
import trFinance from './locales/tr/finance.json'
import trRevenue from './locales/tr/revenue.json'
import trSubConsultants from './locales/tr/sub-consultants.json'
import trInstruments from './locales/tr/instruments.json'
import trReports from './locales/tr/reports.json'
import trLeave from './locales/tr/leave.json'
import trUsers from './locales/tr/users.json'
import trSettings from './locales/tr/settings.json'
import trNotifications from './locales/tr/notifications.json'
import trDocuments from './locales/tr/documents.json'
import trDataEntry from './locales/tr/data-entry.json'
import trMonitor from './locales/tr/monitor.json'
import trEnums from './locales/tr/enums.json'
import trErrors from './locales/tr/errors.json'
import trRoles from './locales/tr/roles.json'
import trDesktop from './locales/tr/desktop.json'
import trDatacenter from './locales/tr/datacenter.json'
import trPortal from './locales/tr/portal.json'
import trAi from './locales/tr/ai.json'

import enCommon from './locales/en/common.json'
import enAuth from './locales/en/auth.json'
import enNav from './locales/en/nav.json'
import enDashboard from './locales/en/dashboard.json'
import enCustomers from './locales/en/customers.json'
import enProposals from './locales/en/proposals.json'
import enProposalTemplates from './locales/en/proposalTemplates.json'
import enProcurement from './locales/en/procurement.json'
import enProjects from './locales/en/projects.json'
import enTimesheets from './locales/en/timesheets.json'
import enFinance from './locales/en/finance.json'
import enRevenue from './locales/en/revenue.json'
import enSubConsultants from './locales/en/sub-consultants.json'
import enInstruments from './locales/en/instruments.json'
import enReports from './locales/en/reports.json'
import enLeave from './locales/en/leave.json'
import enUsers from './locales/en/users.json'
import enSettings from './locales/en/settings.json'
import enNotifications from './locales/en/notifications.json'
import enDocuments from './locales/en/documents.json'
import enDataEntry from './locales/en/data-entry.json'
import enMonitor from './locales/en/monitor.json'
import enEnums from './locales/en/enums.json'
import enErrors from './locales/en/errors.json'
import enRoles from './locales/en/roles.json'
import enDesktop from './locales/en/desktop.json'
import enDatacenter from './locales/en/datacenter.json'
import enPortal from './locales/en/portal.json'
import enAi from './locales/en/ai.json'

export const SUPPORTED_LOCALES = ['tr', 'en'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]
export const DEFAULT_LOCALE: Locale = 'tr'

export const LOCALE_STORAGE_KEY = 'pusula_locale'

const resources = {
  tr: {
    common: trCommon,
    auth: trAuth,
    nav: trNav,
    dashboard: trDashboard,
    customers: trCustomers,
    proposals: trProposals,
    proposalTemplates: trProposalTemplates,
    procurement: trProcurement,
    projects: trProjects,
    timesheets: trTimesheets,
    finance: trFinance,
    revenue: trRevenue,
    'sub-consultants': trSubConsultants,
    instruments: trInstruments,
    reports: trReports,
    leave: trLeave,
    users: trUsers,
    settings: trSettings,
    notifications: trNotifications,
    documents: trDocuments,
    'data-entry': trDataEntry,
    monitor: trMonitor,
    enums: trEnums,
    errors: trErrors,
    roles: trRoles,
    desktop: trDesktop,
    datacenter: trDatacenter,
    portal: trPortal,
    ai: trAi,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    nav: enNav,
    dashboard: enDashboard,
    customers: enCustomers,
    proposals: enProposals,
    proposalTemplates: enProposalTemplates,
    procurement: enProcurement,
    projects: enProjects,
    timesheets: enTimesheets,
    finance: enFinance,
    revenue: enRevenue,
    'sub-consultants': enSubConsultants,
    instruments: enInstruments,
    reports: enReports,
    leave: enLeave,
    users: enUsers,
    settings: enSettings,
    notifications: enNotifications,
    documents: enDocuments,
    'data-entry': enDataEntry,
    monitor: enMonitor,
    enums: enEnums,
    errors: enErrors,
    roles: enRoles,
    desktop: enDesktop,
    datacenter: enDatacenter,
    portal: enPortal,
    ai: enAi,
  },
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES,
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    defaultNS: 'common',
    ns: Object.keys(resources.tr),
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    returnNull: false,
  })

export function setLocale(locale: Locale) {
  i18n.changeLanguage(locale)
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // ignore storage errors (private mode, quota)
  }
  document.documentElement.lang = locale
}

export function getLocale(): Locale {
  const current = (i18n.language || DEFAULT_LOCALE).slice(0, 2).toLowerCase()
  return (SUPPORTED_LOCALES as readonly string[]).includes(current)
    ? (current as Locale)
    : DEFAULT_LOCALE
}

document.documentElement.lang = getLocale()

export default i18n
